//changed due to package upgrade
import React, { useState } from 'react';
import AppGridContainer from '@projectK/components/AppGridContainer';
import {
  alpha,
  Box,
  Button,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Form, Formik } from 'formik';
import EditIcon from '@mui/icons-material/Edit';
import AppCard from '@projectK/components/AppCard';
import jwtAxios from '@projectK/services/auth/JWT';
import { useInfoViewActionsContext } from '@projectK/context';
import AppInfoView from '@projectK/components/AppInfoView';
import * as yup from 'yup';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import PropTypes from 'prop-types';

const validationSchema = yup.object({
  name: yup
    .string()
    .max(100, 'Name should not exceed 100 characters.')
    .nullable(),
  address1: yup
    .string()
    .max(255, 'Address must be at most 255 characters.')
    .nullable(),
  address2: yup
    .string()
    .matches(/^[A-Za-z0-9,\s]+$/, 'Please enter a valid street.')

    .max(255, 'Street must be at most 255 characters.')
    .nullable(),
  state: yup
    .string()
    .max(50, 'State must be at most 50 characters.')
    .matches(/^[A-Za-z]+(?:\s[A-Za-z]+)*$/, 'Please enter a valid state. ')
    .nullable(),
  city: yup
    .string()
    .max(50, 'City must be at most 50 characters.')
    .matches(/^[A-Za-z]+(?:[\s-][A-Za-z]+)*$/, 'Please enter a valid city.')
    .nullable(),
  zip: yup
    .string()
    .matches(/^\d{5,6}$/, 'Zip code is not valid.')
    .nullable(),
});
const Location = ({ settings, fetchSettings }) => {
  const companyTheme = CompanyTheme();
  const [isEdit, setIsEdit] = useState(false);
  const infoViewActionsContext = useInfoViewActionsContext();

  return (
    <>
      <AppCard>
        <Formik
          enableReinitialize={true}
          validateOnChange={true}
          initialValues={{
            name: settings?.name || '',
            address1: settings?.address1 || '',
            address2: settings?.address2 || '',
            city: settings?.city || '',
            state: settings?.state || '',
            zip: settings?.zip || '',
          }}
          validationSchema={validationSchema}
          onSubmit={(values, { setSubmitting }) => {
            setSubmitting(true);
            // Prepare the data for submission
            const settingsArray = Object.keys(values).map((fieldName) => ({
              setting_name: 'location',
              setting_slug: fieldName,
              setting_value:
                values[fieldName] === '' ? null : values[fieldName],
              is_active: true, // Set is_active as needed
            }));
            // Make the PUT request using jwtAxios
            jwtAxios
              .put(`/app-settings/location/`, settingsArray)
              .then((response) => {
                setSubmitting(false);
                setIsEdit(false);
                fetchSettings();
                infoViewActionsContext.showMessage(
                  'Location details updated successfully!',
                );
              })
              .catch((error) => {
                // Handle the error here
                infoViewActionsContext.fetchError(error.message);
                setSubmitting(false);
              });
          }}
        >
          {({
            resetForm,
            setFieldValue,
            errors,
            touched,
            values,
            setValues,
          }) => (
            <Form noValidate autoComplete='off'>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box>
                  <Typography
                    variant='h4'
                    sx={{
                      color: companyTheme
                        ? companyTheme.primary.main
                        : 'text.primary',
                    }}
                  >
                    Location Settings
                  </Typography>
                  <Typography
                    variant='body1'
                    sx={{ mt: 2, color: 'text.secondary' }}
                  >
                    These settings control the location information that is
                    displayed.
                  </Typography>
                </Box>
                <Box>
                  {!isEdit && (
                    <EditIcon
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        setIsEdit(true);
                      }}
                    />
                  )}
                </Box>
              </Box>
              <Divider sx={{ my: 4 }} />
              <AppGridContainer>
                <Grid size={{ xs: 12, md: 12 }}>
                  <Box
                    sx={{
                      border: '1px solid #EAECF0',
                      p: 6,
                      borderRadius: 3,
                      boxShadow:
                        '0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)',
                    }}
                  >
                    <AppGridContainer>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          name='name'
                          variant='outlined'
                          value={values.name}
                          onChange={(event) => {
                            setFieldValue('name', event.target.value);
                            touched.name = true;
                          }}
                          error={touched.name && Boolean(errors.name)}
                          helperText={touched.name && errors.name}
                          sx={{
                            width: '100%',
                          }}
                          label='Name'
                          slotProps={{
                            input: {
                              readOnly: !isEdit,
                            },
                          }}
                          inputProps={{ maxLength: 101 }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          name='address2'
                          variant='outlined'
                          value={values.address2}
                          onChange={(event) => {
                            setFieldValue('address2', event.target.value);
                            touched.address2 = true;
                          }}
                          error={touched.address2 && Boolean(errors.address2)}
                          helperText={touched.address2 && errors.address2}
                          sx={{
                            width: '100%',
                          }}
                          label='Street'
                          slotProps={{
                            input: {
                              readOnly: !isEdit,
                            },
                          }}
                          inputProps={{ maxLength: 256 }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 12 }}>
                        <TextField
                          name='address1'
                          variant='outlined'
                          value={values.address1}
                          onChange={(event) => {
                            setFieldValue('address1', event.target.value);
                            touched.address1 = true;
                          }}
                          error={touched.address1 && Boolean(errors.address1)}
                          helperText={touched.address1 && errors.address1}
                          sx={{
                            width: '100%',
                          }}
                          label='Address'
                          slotProps={{
                            input: {
                              readOnly: !isEdit,
                            },
                          }}
                          inputProps={{ maxLength: 256 }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          name='city'
                          variant='outlined'
                          value={values.city}
                          onChange={(event) => {
                            setFieldValue('city', event.target.value);
                            touched.city = true;
                          }}
                          error={touched.city && Boolean(errors.city)}
                          helperText={touched.city && errors.city}
                          sx={{
                            width: '100%',
                          }}
                          label='City'
                          slotProps={{
                            input: {
                              readOnly: !isEdit,
                            },
                          }}
                          inputProps={{ maxLength: 51 }}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          name='state'
                          variant='outlined'
                          value={values.state}
                          onChange={(event) => {
                            setFieldValue('state', event.target.value);
                            touched.state = true;
                          }}
                          error={touched.state && Boolean(errors.state)}
                          helperText={touched.state && errors.state}
                          sx={{
                            width: '100%',
                          }}
                          label='State'
                          slotProps={{
                            input: {
                              readOnly: !isEdit,
                            },
                          }}
                          inputProps={{ maxLength: 51 }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          name='zip'
                          variant='outlined'
                          value={values.zip}
                          onChange={(event) => {
                            setFieldValue('zip', event.target.value);
                            touched.zip = true;
                          }}
                          error={touched.zip && Boolean(errors.zip)}
                          helperText={touched.zip && errors.zip}
                          sx={{
                            width: '100%',
                          }}
                          label='Zip'
                          slotProps={{
                            input: {
                              readOnly: !isEdit,
                            },
                          }}
                          inputProps={{ maxLength: 6 }}
                        />
                      </Grid>
                    </AppGridContainer>
                  </Box>
                </Grid>
              </AppGridContainer>
              {isEdit && (
                <Stack
                  direction='row'
                  justifyContent='flex-end'
                  spacing={5}
                  sx={{ mt: 3 }}
                >
                  <Button
                    sx={{
                      borderColor: companyTheme
                        ? companyTheme.primary.main
                        : '#2196f3',
                      '&:hover': {
                        borderColor: companyTheme
                          ? alpha(companyTheme.primary.main, 0.8)
                          : alpha('#2196f3', 0.8),
                      },
                      color: companyTheme
                        ? companyTheme.primary.main
                        : '#2196f3',
                    }}
                    onClick={() => {
                      resetForm();
                      setIsEdit(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant='contained'
                    color='primary'
                    type='submit'
                    sx={{
                      backgroundColor: companyTheme
                        ? companyTheme.primary.main
                        : '#2196f3',
                      '&:hover': {
                        backgroundColor: companyTheme
                          ? alpha(companyTheme.primary.main, 0.8) // Adjust the alpha value as needed
                          : alpha('#2196f3', 0.8),
                      },
                    }}
                  >
                    Save
                  </Button>
                </Stack>
              )}
            </Form>
          )}
        </Formik>
      </AppCard>
      <AppInfoView />
    </>
  );
};
export default Location;
Location.propTypes = {
  settings: PropTypes.object,
  fetchSettings: PropTypes.func,
};
